import { createPortal } from 'react-dom';
import { Menu, Transition } from '@headlessui/react';
import { Fragment, forwardRef, useEffect, useState } from 'react';

const DropdownPortal = forwardRef(({ children, buttonRef, onClose }, ref) => {
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!buttonRef.current) return;

    const updatePosition = () => {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const contentHeight = 150; // تقدير تقريبي لارتفاع القائمة

      if (spaceBelow >= contentHeight || spaceBelow >= spaceAbove) {
        setPosition({
          top: buttonRect.bottom + 4,
          left: Math.max(10, buttonRect.left),
          placement: 'bottom'
        });
      } else {
        setPosition({
          top: buttonRect.top - contentHeight - 4,
          left: Math.max(10, buttonRect.left),
          placement: 'top'
        });
      }
    };

    // تحديث الموقع فوراً
    updatePosition();

    // تحديث الموقع عند التمرير أو تغيير الحجم
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [buttonRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!buttonRef?.current || !ref?.current) return;

      const clickedOnButton = buttonRef.current.contains(event.target);
      const clickedOnDropdown = ref.current.contains(event.target);

      if (!clickedOnButton && !clickedOnDropdown) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [buttonRef, onClose, ref]);

  // لا نعرض القائمة حتى يتم حساب موقعها
  if (!buttonRef.current || !position) return null;

  return createPortal(
    <div
      ref={ref}
      className="fixed z-50"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        visibility: position ? 'visible' : 'hidden'
      }}
    >
      <Transition
        show={!!position}
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-36 ${
          position.placement === 'top' ? 'origin-bottom' : 'origin-top'
        }`}>
          {children}
        </div>
      </Transition>
    </div>,
    document.body
  );
});

DropdownPortal.displayName = 'DropdownPortal';

export default DropdownPortal;
